import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { colors } from "sexy";
import { useState } from "react";
import {
  ControlPoint,
  Project,
  ProjectHistoryEvent,
  ProjectPhoto,
} from "types";
import { getFlutterTime } from "@services/timeHelpers";
import { useProjectStore } from "@store/projectStore";
import { EmailModal } from "./EmailModal";
import { PhotoModal } from "./PhotoModal";
import DropDown from "@components/ui/dropdown/DropDown";
import { DropDownItem } from "@components/ui/dropdown/DropDownMultiple";
import { CustomImage } from "@components/ui/images/CustomImage";
import Dropzone from "@components/ui/blocks/Dropzone";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { useDataStore } from "@store/user_org";
import { projectsApi } from "@services/api";
import { Timestamp } from "firebase/firestore";
import { handleSendFastEmailClient as sendFastEmail, fillVariablesInEmailTemplate, handleFetchEmailOptions } from "helpers/emailHelpers";
import { showNotification } from "context/notification.service";

export const StepModal = ({
  onClose,
  theme_name,
  step_name,
  controlpoint,
  handleSave,
  activeProject,
  setActiveProject,
  userData,
  orgData,
  sentEmails,
  setSentEmails,
}: {
  onClose: () => void;
  theme_name: string;
  step_name: string;
  controlpoint: ControlPoint;
  handleSave: (updatedControlPoint: ControlPoint) => void;
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  userData?: any;
  orgData?: any;
  sentEmails?: any[];
  setSentEmails?: (emails: any[]) => void;
}) => {
  const storeUserData = useDataStore((state) => state.userData);
  const storeOrgData = useDataStore((state) => state.orgData);
  const {
    sentEmails: storeSentEmails,
    setSentEmails: storeSetSentEmails,
    emailTemplates,
  } = useProjectStore();

  const [localControlPoint, setLocalControlPoint] =
    useState<ControlPoint>(controlpoint);
  const [emailSent, setEmailSent] = useState<Timestamp | undefined>(undefined);
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);

  const getLocalFromFlutter = (date: any) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSendFastEmailClient = () => {
    const currentUserData = userData || storeUserData;
    const currentOrgData = orgData || storeOrgData;
    if (!activeProject || !currentUserData || !currentOrgData) {
      showNotification({
        type: "error",
        message: "Données manquantes pour l'envoi",
      });
      return;
    }

    const { updatedProject, new_email_sent } = sendFastEmail(
      controlpoint,
      activeProject,
      currentUserData,
      currentOrgData,
    );

    if (!new_email_sent) {
      showNotification({
        type: "error",
        message: "Aucun modèle d'email associé à ce point de contrôle",
      });
      return;
    }

    // Persist email to backend
    const addSentEmail = useProjectStore.getState().addSentEmail;
    addSentEmail(new_email_sent, currentUserData.org_id || "");

    // Update project with history event + controlpoint email status
    if (setActiveProject) {
      setActiveProject(updatedProject);
    }

    // Sync localControlPoint so "Enregistrer" doesn't overwrite the email status
    const updatedCp = updatedProject.themes
      .flatMap((t) => t.steps)
      .flatMap((s) => s.controlpoints)
      .find((cp) => cp.id === controlpoint.id);
    if (updatedCp) setLocalControlPoint(updatedCp);

    setEmailSent(getFlutterTime());
    showNotification({
      type: "success",
      message: "Email envoyé au client",
    });
  };

  const handleValidate = async () => {
    if (
      localControlPoint.state === "completed" &&
      controlpoint.associated_email_template_id &&
      controlpoint.email?.sent !== true &&
      !emailSent
    ) {
      // Auto-send email then validate
      const currentUserData = userData || storeUserData;
      const currentOrgData = orgData || storeOrgData;
      if (!activeProject || !currentUserData || !currentOrgData) {
        showNotification({
          type: "error",
          message: "Données manquantes pour l'envoi",
        });
        return;
      }

      try {
        // Get email template and fill it
        const { emailTemplates, libraryDocuments, fetchSentEmails } = useProjectStore.getState();
        const template = emailTemplates.find(
          (model) => model.id === controlpoint.associated_email_template_id,
        );

        if (!template) {
          showNotification({
            type: "error",
            message: "Aucun template d'email associé à ce point de contrôle",
          });
          return;
        }

        const filledTemplate = fillVariablesInEmailTemplate(
          template,
          activeProject,
          currentUserData,
          currentOrgData,
        );

        const documents = libraryDocuments.filter((doc) =>
          template?.library_documents_id?.includes(doc.id),
        ) || [];

        const clientEmails = handleFetchEmailOptions(activeProject)
          .filter((option) => option.label.includes("(client)"))
          .map((option) => option.value);

        // Send email with automatic history event and controlpoint update
        const projectAfterEmail = await projectsApi.sendEmailWithHistory(
          activeProject.id,
          {
            id: "temp_email_id_" + Date.now(),
            subject: filledTemplate.subject,
            body: filledTemplate.body,
            org_id: currentOrgData.id,
            project_id: activeProject.id,
            sender_user_id: currentUserData.id,
            email_template_id: template.id,
            emails_to: clientEmails,
            files: documents.map((doc) => doc.file),
            controlpoint_id: controlpoint.id,
          }
        );

        // Refresh sent emails list
        await fetchSentEmails(currentOrgData.id, activeProject.id);

        // Update controlpoint state to completed (adds validation history event)
        const finalProject = await projectsApi.updateControlpointState(
          activeProject.id,
          controlpoint.id,
          "completed"
        );

        if (setActiveProject) setActiveProject(finalProject);
        showNotification({
          type: "success",
          message: "Email envoyé et étape validée",
        });
        onClose();
      } catch (error) {
        console.error("Error sending email and validating step:", error);
        showNotification({
          type: "error",
          message: "Erreur lors de l'envoi de l'email et validation",
        });
      }
    } else {
      handleSave(localControlPoint);
      onClose();
    }
  };

  return (
    <BaseModal
      title={"Modifier le point de contrôle"}
      startIcon={<Icon1 name="modify" size={20} />}
      onClose={onClose}
    >
      {selectedPhotoId && activeProject && (
        <PhotoModal
          onClose={() => setSelectedPhotoId(null)}
          photo_id={selectedPhotoId}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
        />
      )}
      {photoPickerOpen && activeProject && (
        <PhotoPickerModal
          activeProject={activeProject}
          setActiveProject={setActiveProject}
          alreadyLinkedIds={localControlPoint.photos_id || []}
          userData={userData || storeUserData}
          onLink={(photoIds) => {
            setLocalControlPoint({
              ...localControlPoint,
              photos_id: [...(localControlPoint.photos_id || []), ...photoIds],
            });
            setPhotoPickerOpen(false);
          }}
          onClose={() => setPhotoPickerOpen(false)}
        />
      )}
      {emailOpen && (
        <EmailModal
          email_template_id={controlpoint.associated_email_template_id}
          onClose={() => setEmailOpen(false)}
          controlpoint={controlpoint}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
          userData={userData || storeUserData}
          orgData={orgData || storeOrgData}
          sentEmails={sentEmails || storeSentEmails}
          setSentEmails={setSentEmails || storeSetSentEmails}
          refreshControlPoint={() => {
            setEmailSent(getFlutterTime());
          }}
        />
      )}

      <div className="bg-white flex flex-col gap-4 p-4">
        {/* Header Info */}
        <div className="flex flex-col gap-1">
          <div className="text-sm" style={{ color: colors.neutral_400 }}>
            {theme_name}
          </div>
          <div className="text-sm" style={{ color: colors.neutral_400 }}>
            {step_name}
          </div>
          <div className="text-sm" style={{ color: colors.neutral_500 }}>
            {controlpoint.label}
          </div>
        </div>

        <DropDown
          options={step_options}
          value={localControlPoint.state}
          onSelect={(value) => {
            setLocalControlPoint({
              ...localControlPoint,
              state: value as
                | "completed"
                | "missed"
                | "not_applicable"
                | "default",
            });
          }}
        ></DropDown>

        {/* Hidden from client toggle */}
        <SwitchBrick
          checked={localControlPoint.hidden ?? false}
          onChange={(checked) =>
            setLocalControlPoint({
              ...localControlPoint,
              hidden: checked,
            })
          }
          title="Masquer au client"
          description="Ce point de contrôle ne sera pas visible par le client"
        />

        {/* Email Warning - Not Sent */}
        {controlpoint.associated_email_template_id &&
          controlpoint.email?.sent !== true &&
          !emailSent && (
            <div
              className="w-fit max-w-full rounded-lg border-2 p-2.5"
              style={{
                backgroundColor: colors.neutral_100,
                borderColor: colors.neutral_300,
              }}
            >
              <div className="text-sm " style={{ color: colors.neutral_500 }}>
                Un email doit être envoyé à la validation de ce point de
                contrôle.
              </div>
            </div>
          )}

        {/* Photos */}
        {localControlPoint.photos_id &&
          localControlPoint.photos_id.length > 0 && (
            <div
              className="w-full flex flex-row flex-wrap rounded-2xl p-2 gap-1.5"
              style={{ backgroundColor: colors.neutral_100 }}
            >
              {localControlPoint.photos_id.map((photo_id, index) => {
                const photo = activeProject?.photos.find(
                  (p) => p.id === photo_id,
                );
                return photo ? (
                  <div key={index} className="relative group">
                    <CustomImage
                      source={{ uri: photo.file.url }}
                      className="w-24 h-24 rounded-xl cursor-pointer"
                      hidden={photo.hidden}
                      alt={`Photo ${index + 1}`}
                      onClick={() => setSelectedPhotoId(photo.id)}
                    />
                    <div
                      className="absolute bottom-0 right-0 bg-white hover:bg-neutral-200 rounded-tl-xl rounded-br-lg w-8 h-8 items-center justify-center flex shadow cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocalControlPoint({
                          ...localControlPoint,
                          photos_id: localControlPoint.photos_id?.filter(
                            (id) => id !== photo_id,
                          ),
                        });
                      }}
                      title="Dissocier la photo"
                    >
                      <Icon1 name="unlink" size={20} />
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          )}

        {/* Email Sent Confirmation */}
        {((controlpoint.email?.sent === true && controlpoint.email?.date) ||
          emailSent) && (
          <div
            className="w-full rounded-lg border-2 p-2.5"
            style={{
              backgroundColor: colors.green_100,
              borderColor: colors.green_300,
            }}
          >
            <div className="text-sm" style={{ color: colors.green_500 }}>
              Un email a été envoyé pour ce point de contrôle le{" "}
              {getLocalFromFlutter(controlpoint.email?.date || emailSent)}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-row justify-start w-full gap-2.5 mb-6">
          {emailTemplates.find(
            (model) => model.id === controlpoint.associated_email_template_id,
          )?.destination_type === "client" &&
          controlpoint.email?.sent !== true ? (
            <SecondaryButton
              startIcon={<Icon1 name="send" size={18} />}
              onClick={handleSendFastEmailClient}
            >
              Envoi rapide client
            </SecondaryButton>
          ) : (
            <SecondaryButton onClick={() => setEmailOpen(true)}>
              Envoyer un email
            </SecondaryButton>
          )}
          <SecondaryButton
            startIcon={<Icon1 name="image" size={18} />}
            onClick={() => setPhotoPickerOpen(true)}
          >
            Ajouter une photo
          </SecondaryButton>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Enregistrer" onClick={handleValidate} />
      </div>
    </BaseModal>
  );
};

export const getStateIcon = (
  state: ControlPoint["state"],
  size = 24,
  iconSize = 16,
) => {
  switch (state) {
    case "completed":
      return (
        <div
          style={{
            height: size,
            width: size,
          }}
          className={`
              hover:ring-2 hover:ring-emerald-300
              
              bg-emerald-500
              rounded-md
              transition-all
              flex items-center justify-center
            `}
        >
          <svg
            style={{
              height: iconSize,
              width: iconSize,
            }}
            className=" text-white"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="4"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
      );
    case "missed":
      return (
        <div
          style={{
            height: size,
            width: size,
          }}
          className={`
              hover:ring-2 hover:ring-red-300
              bg-red-100
              border-2 border-red-300
              rounded-md
              transition-all
              flex items-center justify-center
            `}
        >
          <Icon1 name="minus" size={iconSize} color={colors.red_500}></Icon1>
        </div>
      );
    case "not_applicable":
      return (
        <div
          style={{
            height: size,
            width: size,
          }}
          className={`
              hover:ring-2 hover:ring-neutral-300
              bg-neutral-100
              border-2 border-neutral-300
              rounded-md
              transition-all
              flex items-center justify-center
            `}
        >
          <Icon1
            name="minus"
            size={iconSize}
            color={colors.neutral_400}
          ></Icon1>
        </div>
      );
    case "default":
      return (
        <div
          style={{
            height: size,
            width: size,
          }}
          className={`
              hover:ring-2 hover:ring-neutral-300
              border-2 border-neutral-300
              rounded-md
              bg-white
              transition-all
              flex items-center justify-center
            `}
        ></div>
      );
  }
};

const PhotoPickerModal = ({
  activeProject,
  setActiveProject,
  alreadyLinkedIds,
  onLink,
  onClose,
  userData,
}: {
  activeProject: Project;
  setActiveProject?: (project: Project) => void;
  alreadyLinkedIds: string[];
  onLink: (photoIds: string[]) => void;
  onClose: () => void;
  userData?: any;
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const availablePhotos = activeProject.photos.filter(
    (p) => !alreadyLinkedIds.includes(p.id),
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleUploadAndLink = async () => {
    let newPhotoIds: string[] = [];
    let newPhotos: ProjectPhoto[] = [];

    // Upload new files if any
    if (droppedFiles.length > 0) {
      setUploading(true);
      try {
        const uploaded = await projectsApi.uploadFiles(
          activeProject.id,
          droppedFiles,
        );
        const now = Timestamp.now();

        newPhotos = uploaded.map((file) => ({
          id: file.id,
          file: {
            id: file.id,
            url: file.url,
            name: file.name,
            created_at: now,
            octets: file.size,
          },
        }));

        newPhotoIds = newPhotos.map((p) => p.id);
      } catch (error) {
        console.error("Error uploading photos:", error);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const allPhotoIds = [...selectedIds, ...newPhotoIds];

    // Update project: add new photos + history event only for newly uploaded photos
    if (setActiveProject) {
      let updatedHistoryEvents = activeProject.history_events || [];

      if (newPhotoIds.length > 0) {
        const historyEvent: ProjectHistoryEvent = {
          id: Math.random().toString(),
          action: "a ajouté des photos",
          date: getFlutterTime(),
          photos_id: newPhotoIds,
          user: {
            id: userData?.id || "unknown",
            display_name:
              userData?.infos?.display_name || "Utilisateur inconnu",
            photo_url: userData?.customization?.photo_url,
          },
        };
        updatedHistoryEvents = [historyEvent, ...updatedHistoryEvents];
      }

      setActiveProject({
        ...activeProject,
        photos: [...activeProject.photos, ...newPhotos],
        history_events: updatedHistoryEvents,
      });
    }

    onLink(allPhotoIds);
  };

  return (
    <BaseModal
      title="Ajouter une photo"
      startIcon={<Icon1 name="image" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-4 p-4">
        {/* Existing gallery */}
        {availablePhotos.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-h3 text-neutral-500 font-medium">
              Photos du projet
            </div>
            <div className="flex flex-row flex-wrap gap-2">
              {availablePhotos.map((photo) => {
                const isSelected = selectedIds.includes(photo.id);
                return (
                  <div
                    key={photo.id}
                    className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                      isSelected
                        ? "border-blue-500 ring-2 ring-blue-200"
                        : "border-transparent hover:border-neutral-300"
                    }`}
                    onClick={() => toggleSelect(photo.id)}
                  >
                    <CustomImage
                      source={{ uri: photo.file.url }}
                      className="w-100 h-100"
                      hidden={photo.hidden}
                      alt={photo.file.name}
                    />
                    {isSelected && (
                      <div className="absolute top-1 right-1 bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {availablePhotos.length === 0 && (
          <div className="text-h3 text-neutral-400 py-2">
            Toutes les photos du projet sont déjà associées
          </div>
        )}

        {/* Upload new */}
        <div className="flex flex-col gap-2">
          <div className="text-h3 text-neutral-500 font-medium">
            Télécharger une nouvelle photo
          </div>
          <Dropzone
            onDrop={(files) => setDroppedFiles(files)}
            files={droppedFiles}
            onDelete={(file) =>
              setDroppedFiles(droppedFiles.filter((f) => f !== file))
            }
            showImagePreview={true}
            acceptTypes={{
              "image/jpeg": [".jpg", ".jpeg"],
              "image/png": [".png"],
              "image/webp": [".webp"],
            }}
          />
        </div>
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={uploading ? "Téléchargement..." : "Ajouter"}
          onClick={handleUploadAndLink}
          disabled={selectedIds.length === 0 && droppedFiles.length === 0}
        />
      </div>
    </BaseModal>
  );
};

const step_options: DropDownItem[] = [
  {
    value: "completed",
    label: (
      <div className="flex flex-row items-center gap-4">
        {getStateIcon("completed", 25, 18)}
        <span>Terminé</span>
      </div>
    ),
  },
  {
    value: "missed",
    label: (
      <div className="flex flex-row items-center gap-4">
        {getStateIcon("missed", 25, 18)}
        <span>Non conforme</span>
      </div>
    ),
  },
  {
    value: "not_applicable",
    label: (
      <div className="flex flex-row items-center gap-4">
        {getStateIcon("not_applicable", 25, 18)}
        <span>Non applicable</span>
      </div>
    ),
  },
  {
    value: "default",
    label: (
      <div className="flex flex-row items-center gap-4">
        {getStateIcon("default", 25, 18)}
        <span>À faire</span>
      </div>
    ),
  },
];
